import {
  Banknote,
  BookOpen,
  Building2,
  CircleUser,
  FilePenLine,
  Home,
  Landmark,
  LifeBuoy,
  ListChecks,
  LoaderCircle,
  Lock,
  LogOut,
  Mail,
  NotepadTextDashed,
  Plus,
  Settings,
  SquareUser,
  Wallet,
  type LucideIcon,
} from 'lucide-react'
import { useState } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  SidebarSeparator,
} from '@/components/ui/sidebar'
import { useToast } from '@/components/ui/use-toast'
import { useAccountingQuestions } from '@/features/accounting/hooks/use-accounting-questions'
import { ACCOUNTING_NAV } from '@/features/accounting/lib/nav'
import { dataLayerService } from '@/features/analytics'
import { EIN_CORRECTION_HREF } from '@/features/ein-correction'
import { useHasEinCorrection } from '@/features/ein-correction/lib/use-ein-correction'
import { useFormationOrder } from '@/features/formation'
import { useCompanyPresence } from '@/hooks/useCompanyPresence'
import { useAppSignOut, useAppUser } from '@/lib/auth/client'
import { StripeApiService } from '@/lib/http/stripe-api.service'
import Link from '@/lib/navigation/link'
import { usePathname } from '@/lib/navigation/navigation'
import { cn } from '@/lib/utils'
import { useGeneralStore } from '@/store/useGeneralStore'
import { OnboardingStatus } from '@/types/enums'
import { SUPPORT_EMAIL } from '@/utils/constants'

import { SIGN_OUT_FAILED_TOAST } from './CustomerNavbar'

interface NavItem {
  title: string
  href: string
  icon: LucideIcon
  isActive: (pathname: string) => boolean
  /** Rendered as a sub-menu, and only while `isActive` holds. */
  children?: Array<{ title: string; href: string }>
  /** Things waiting on the customer here: a count pill, and a dot on the icon when collapsed. */
  count?: { value: number; label: string }
}

/**
 * The count pill on a nav item: hidden at zero, capped at 99+. Screen readers get the full label as
 * text in the link, in every mode (the pill gives way to a dot when the sidebar collapses to icons).
 */
function NavItemCount({ count }: { count: NavItem['count'] }) {
  if (!count || count.value <= 0) return null
  return (
    <>
      <span className="sr-only">{count.label}</span>
      <Badge
        variant="amber"
        aria-hidden="true"
        className="ml-auto shrink-0 tabular-nums group-data-[collapsible=icon]:hidden"
      >
        {count.value > 99 ? '99+' : count.value}
      </Badge>
      <span
        aria-hidden="true"
        className="absolute top-1 right-1 hidden size-2 rounded-full bg-warning group-data-[collapsible=icon]:block"
      />
    </>
  )
}

interface NavGroup {
  label: string
  items: NavItem[]
  /** Account-level controls stay reachable while formation is still in progress. */
  exemptFromFormationLock?: boolean
}

/**
 * The company's mark in the header: its initial on a forest tile, the way a
 * workspace switcher marks a workspace. Named companies only.
 */
function CompanyMonogram({ name }: { name: string }) {
  return (
    <div
      data-slot="company-monogram"
      aria-hidden="true"
      className="flex aspect-square size-8 shrink-0 items-center justify-center rounded-control bg-primary text-sm font-semibold text-primary-foreground shadow-chip"
    >
      {name.trim().charAt(0).toUpperCase()}
    </div>
  )
}

/** The header tile for a slot with no company to mark: an outline waiting to be filled. */
/** A sub-item is active on its own page and on pages beneath it (an account's transactions keep Accounts lit). */
function isChildActive(pathname: string, href: string): boolean {
  return pathname === href || (href !== '/accounting' && pathname.startsWith(`${href}/`))
}

function EmptyCompanyTile({ children }: { children?: React.ReactNode }) {
  return (
    <div
      aria-hidden="true"
      className="flex aspect-square size-8 shrink-0 items-center justify-center rounded-control border border-dashed border-input text-muted-foreground"
    >
      {children}
    </div>
  )
}

/**
 * The signed-in customer and a sign-out button. No account menu: password,
 * Google, passkeys, emails and cookies live on /account/settings, Support is its own footer item.
 */
function NavUser() {
  const signOut = useAppSignOut()
  const { user } = useAppUser()
  const { toast } = useToast()

  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ')
  const email = user?.emailAddresses?.[0]?.emailAddress ?? ''

  const handleSignOut = async () => {
    try {
      dataLayerService.push('sign_out')
      await signOut()
    } catch (error) {
      console.error('Logout error:', error)
      toast(SIGN_OUT_FAILED_TOAST)
    }
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <div className="flex items-center gap-2 px-2 py-1.5 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:p-0">
          <CircleUser
            aria-hidden
            className="size-8 shrink-0 rounded-lg p-0.5 text-sidebar-foreground group-data-[collapsible=icon]:hidden"
          />
          <div className="grid min-w-0 flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
            <span className="truncate font-medium text-sidebar-foreground">{fullName}</span>
            <span className="truncate text-xs text-sidebar-foreground/70">{email}</span>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label="Sign out"
            title="Sign out"
            className="shrink-0"
            onClick={() => void handleSignOut()}
          >
            <LogOut />
          </Button>
        </div>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}

export function CustomerSidebar(props: React.ComponentProps<typeof Sidebar>) {
  const { toast } = useToast()
  const pathname = usePathname() ?? ''
  const [isLoading, setIsLoading] = useState(false)
  const { companyDetail } = useGeneralStore((state) => state)
  const shouldFetchFormationOrder = pathname.startsWith('/onboarding') || Boolean(companyDetail?.isWaitingForFormation)
  const { order: formationOrder } = useFormationOrder({ enabled: shouldFetchFormationOrder })

  // Determine if the user is in formation process (not completed). Use display_status
  // (service_data.formation.status), not the legacy status column.
  const isInFormationProcess =
    formationOrder && formationOrder.display_status !== 'completed' && formationOrder.display_status !== 'cancelled'
  const shouldLockMenus = isInFormationProcess || companyDetail?.isWaitingForFormation

  const companyName = [companyDetail?.business_name, companyDetail?.designator].filter(Boolean).join(' ')
  const companyPresence = useCompanyPresence()
  const onboardingStatus = useGeneralStore((state) => state.customerBootstrapCache?.onboardingStatus)
  // The slot above the nav holds the company's name. Without one it would be an
  // empty tile. An account with no company still in onboarding gets a link to
  // finish; one that finished onboarding says so plainly (/onboarding would send
  // it straight back to /, and the page body offers support). A failed read or a
  // nameless company shows nothing.
  const companySlot: 'name' | 'setup' | 'none-yet' | null = companyName
    ? 'name'
    : companyPresence !== 'missing'
      ? null
      : onboardingStatus === OnboardingStatus.Completed
        ? 'none-yet'
        : 'setup'

  // One State filings item for every supported state; the page itself names the
  // filing (Wyoming annual report, Delaware franchise tax or LLC annual tax).
  const stateTaxItem: NavItem | null =
    companyDetail?.formation_state === 'DE' || companyDetail?.formation_state === 'WY'
      ? {
          title: 'State filings',
          href: '/state-filings',
          icon: NotepadTextDashed,
          isActive: (p) => p === '/state-filings' || p.startsWith('/state-filings/'),
        }
      : null

  // Name correction is a service only some companies buy (owner 2026-10-17): the Services page
  // offers it, and the sidebar lists it only once the company has a request that is not cancelled,
  // so a customer mid-correction can get back to it. The direct link works either way.
  const hasEinCorrection = useHasEinCorrection(shouldLockMenus ? undefined : companyDetail?.company_id)
  const einCorrectionItem: NavItem | null = hasEinCorrection
    ? {
        title: 'Name correction',
        href: EIN_CORRECTION_HREF,
        icon: FilePenLine,
        isActive: (p) => p === EIN_CORRECTION_HREF || p.startsWith(`${EIN_CORRECTION_HREF}/`),
      }
    : null

  // Open bookkeeping questions keep transactions out of the books, so the Accounting item counts
  // them on every page. The same cached list the questions page reads (and refreshes after an
  // answer); loading, a failure or a 404 (no company yet) count nothing. Not read while locked.
  const openQuestions = useAccountingQuestions({ enabled: !shouldLockMenus }).data?.questions.length ?? 0

  const taxItems: NavItem[] = [
    {
      title: 'Federal tax filing',
      href: '/federal-tax',
      icon: Landmark,
      isActive: (p) => p === '/federal-tax' || p.startsWith('/federal-tax/'),
    },
    ...(stateTaxItem ? [stateTaxItem] : []),
  ]

  const groups: NavGroup[] = [
    {
      label: 'Taxes',
      items: taxItems,
    },
    {
      label: 'Company information',
      items: [{ title: 'Company profile', href: '/company', icon: Building2, isActive: (p) => p === '/company' }],
    },
    {
      label: 'Services',
      items: [
        { title: 'Services', href: '/services', icon: SquareUser, isActive: (p) => p === '/services' },
        { title: 'Orders', href: '/orders', icon: ListChecks, isActive: (p) => p === '/orders' },
        ...(einCorrectionItem ? [einCorrectionItem] : []),
        {
          title: 'Accounting',
          href: '/accounting',
          icon: BookOpen,
          isActive: (p) => p === '/accounting' || p.startsWith('/accounting/'),
          children: ACCOUNTING_NAV.map(({ title, href }) => ({ title, href })),
          count: shouldLockMenus
            ? undefined
            : { value: openQuestions, label: `${openQuestions} open question${openQuestions === 1 ? '' : 's'}` },
        },
        { title: 'Banking', href: '/banking', icon: Banknote, isActive: (p) => p === '/banking' },
        { title: 'Mailroom', href: '/mailroom', icon: Mail, isActive: (p) => p === '/mailroom' },
      ],
    },
    {
      label: 'Account',
      exemptFromFormationLock: true,
      items: [
        { title: 'Settings', href: '/account/settings', icon: Settings, isActive: (p) => p === '/account/settings' },
      ],
    },
  ]

  const lockClass = shouldLockMenus ? 'pointer-events-none opacity-50' : ''

  const goToBillingPortal = async () => {
    const billingWindow = window.open('about:blank', '_blank')
    if (billingWindow) {
      billingWindow.opener = null
    }
    setIsLoading(true)

    try {
      const { url } = await StripeApiService.BillingPortal()
      if (billingWindow) {
        billingWindow.location.href = url
      } else {
        window.location.assign(url)
      }
    } catch (error) {
      billingWindow?.close()
      console.error(error)
      toast({
        variant: 'destructive',
        title: "We couldn't open billing",
        description: `Try again in a minute. If it keeps failing, write to ${SUPPORT_EMAIL}.`,
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Sidebar variant="inset" collapsible="icon" {...props}>
      {companySlot ? (
        <>
          <SidebarHeader>
            <SidebarMenu>
              <SidebarMenuItem>
                {companySlot === 'name' ? (
                  <SidebarMenuButton size="lg" render={<div />}>
                    <CompanyMonogram name={companyDetail?.business_name ?? companyName} />
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-semibold text-foreground">{companyName}</span>
                    </div>
                  </SidebarMenuButton>
                ) : companySlot === 'none-yet' ? (
                  <SidebarMenuButton size="lg" render={<div />}>
                    <EmptyCompanyTile />
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate text-muted-foreground">No company yet</span>
                    </div>
                  </SidebarMenuButton>
                ) : (
                  <SidebarMenuButton size="lg" render={<Link href="/onboarding" prefetch={false} />}>
                    <EmptyCompanyTile>
                      <Plus className="size-4" />
                    </EmptyCompanyTile>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-medium text-primary">Set up your company</span>
                    </div>
                  </SidebarMenuButton>
                )}
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarHeader>

          <SidebarSeparator />
        </>
      ) : null}

      <SidebarContent>
        <div className={lockClass}>
          <SidebarGroup>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  isActive={pathname === '/'}
                  render={
                    <Link
                      href="/"
                      prefetch={false}
                      aria-current={pathname === '/' ? 'page' : undefined}
                      aria-disabled={shouldLockMenus || undefined}
                      data-active={pathname === '/' || undefined}
                      tabIndex={shouldLockMenus ? -1 : undefined}
                      onClick={shouldLockMenus ? (event) => event.preventDefault() : undefined}
                    />
                  }
                >
                  <Home className={cn(pathname === '/' && 'text-primary')} />
                  <span>Dashboard</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroup>
        </div>

        {groups.map((group) => {
          const isGroupLocked = Boolean(shouldLockMenus) && !group.exemptFromFormationLock

          return (
            <div key={group.label} className={isGroupLocked ? lockClass : ''}>
              <SidebarGroup>
                <SidebarGroupLabel className="flex items-center">
                  <span>{group.label}</span>
                  {isGroupLocked && <Lock aria-hidden className="ml-auto size-3 text-muted-foreground" />}
                </SidebarGroupLabel>
                <SidebarMenu>
                  {group.items.map((item) => {
                    const isItemActive = item.isActive(pathname)
                    return (
                      <SidebarMenuItem key={item.href}>
                        <SidebarMenuButton
                          isActive={isItemActive}
                          className={item.count ? 'relative' : undefined}
                          render={
                            <Link
                              href={item.href}
                              prefetch={false}
                              aria-current={isItemActive ? 'page' : undefined}
                              aria-disabled={isGroupLocked || undefined}
                              data-active={isItemActive || undefined}
                              tabIndex={isGroupLocked ? -1 : undefined}
                              onClick={isGroupLocked ? (event) => event.preventDefault() : undefined}
                            />
                          }
                        >
                          <item.icon className={cn(isItemActive && 'text-primary')} />
                          <span>{item.title}</span>
                          <NavItemCount count={item.count} />
                        </SidebarMenuButton>
                        {/* Only while this section is open: seven permanent sub-items would
                          double the sidebar's length for every customer, entitled or not. */}
                        {item.children && isItemActive ? (
                          <SidebarMenuSub>
                            {item.children.map((child) => (
                              <SidebarMenuSubItem key={child.href}>
                                <SidebarMenuSubButton
                                  isActive={isChildActive(pathname, child.href)}
                                  render={
                                    <Link
                                      href={child.href}
                                      prefetch={false}
                                      aria-current={isChildActive(pathname, child.href) ? 'page' : undefined}
                                    />
                                  }
                                >
                                  <span>{child.title}</span>
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                            ))}
                          </SidebarMenuSub>
                        ) : null}
                      </SidebarMenuItem>
                    )
                  })}
                </SidebarMenu>
              </SidebarGroup>
            </div>
          )
        })}
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton render={<a href={`mailto:${SUPPORT_EMAIL}`} target="_blank" rel="noreferrer" />}>
              <LifeBuoy />
              <span>Support</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={() => goToBillingPortal()} disabled={isLoading}>
              {isLoading ? <LoaderCircle className="animate-spin" /> : <Wallet />}
              <span>Billing</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        <div className="max-md:hidden">
          <SidebarSeparator />
          <NavUser />
        </div>
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  )
}
